import { FC, Fragment, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  countGiftBrandSellerDeliveries,
  listGiftBrandSellerDeliveries,
  updateGiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.controller';
import {
  GiftDeliveryForAdmin,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { giftItemBrandListDataAtom } from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { useAsyncFn } from 'react-use';

import {
  nextStatusLabel,
  nextStatusMap,
  prevStatusLabel,
  prevStatusMap,
  statusLabel,
  typeLabel,
} from '../../giftSeller/utils/delivery.constants';
import {
  formatPhone,
  formatRelativeDateTime,
  formatShortDateTime,
  getItemName,
  getOrdererInfo,
  isoToKstInput,
  nowForInput,
} from '../../giftSeller/utils/delivery.utils';
import { GiftBrandThanksCardModal } from '../components/GiftBrandThanksCardModal';

// 송장 입력 모달 상태
// - mode 'advance': 준비중 -> 배송중 전환과 함께 송장 입력 (발송시각=현재)
// - mode 'edit'   : 이미 배송중/배송완료인 건의 송장만 수정 (상태/발송·완료시각 보존)
interface ShippingModalState {
  deliveryId: string;
  carrier: string;
  trackingNumber: string;
  mode: 'advance' | 'edit';
  // edit 모드에서 유지할 현재 상태 및 보존할 기존 시각 (ISO)
  status?: GiftDeliveryStatus;
  shippedAt?: string;
  deliveredAt?: string;
}

// 배송완료 처리 모달 상태 (배송중 -> 배송완료, 또는 완료건의 완료시각 수정)
interface DeliverModalState {
  deliveryId: string;
  deliveredAt: string; // datetime-local 입력값 (YYYY-MM-DDTHH:mm)
  isEdit?: boolean; // 이미 배송완료된 건의 완료시각만 수정하는 경우 true
  bulkIds?: string[]; // 있으면 여러 건에 동일 완료시각 일괄 적용
}

// KST 시각 유틸(nowForInput/isoToKstInput/formatRelativeDateTime/formatShortDateTime)은
// 에이전시 배송관리 페이지와 공용으로 delivery.utils 에서 가져온다.

const DT_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^(\d{2}):(\d{2})$/;
// 입력 숫자(HHmm)를 'HH:mm' 마스크로 정렬
const maskTime = (raw: string) => {
  const d = raw.replace(/\D/g, '').slice(0, 4);
  return d.length <= 2 ? d : `${d.slice(0, 2)}:${d.slice(2, 4)}`;
};

/**
 * 날짜는 캘린더(antd DatePicker)로 선택, 시간은 'HH:mm' 직접 입력.
 * value/onChange 는 내부 'YYYY-MM-DDTHH:mm' 문자열(한국 시각 벽시계) 사용.
 */
const KstDateTimeField: FC<{
  value: string;
  onChange: (v: string) => void;
}> = ({ value, onChange }) => {
  const m = DT_RE.exec(value) ?? DT_RE.exec(nowForInput());
  const dateStr = m ? `${m[1]}-${m[2]}-${m[3]}` : '';
  const timeStr = m ? `${m[4]}:${m[5]}` : '';

  const [timeText, setTimeText] = useState(timeStr);
  // 모달이 새로 열리거나 외부에서 값이 바뀌면 시간 표시값 동기화
  useEffect(() => setTimeText(timeStr), [timeStr]);

  // 날짜+시간이 모두 유효할 때만 상위로 반영
  const emit = (d: string, t: string) => {
    if (!DATE_RE.test(d) || !TIME_RE.test(t)) return;
    onChange(`${d}T${t}`);
  };

  return (
    <div className="flex flex-nowrap items-center gap-2">
      <DatePicker
        value={dateStr ? dayjs(dateStr) : null}
        format="YYYY-MM-DD"
        allowClear={false}
        inputReadOnly
        onChange={(d) => d && emit(d.format('YYYY-MM-DD'), timeText)}
        className="w-36 shrink-0"
        size="large"
      />
      <input
        type="text"
        inputMode="numeric"
        value={timeText}
        placeholder="HH:mm"
        maxLength={5}
        onChange={(e) => {
          const masked = maskTime(e.target.value);
          setTimeText(masked);
          emit(dateStr, masked);
        }}
        onFocus={(e) => e.target.select()}
        onBlur={() => setTimeText(timeStr)}
        className="w-32 rounded-md border border-gray-300 px-2 py-2 text-center text-base focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
      />
      <span className="shrink-0 text-xs text-gray-500">시:분</span>
    </div>
  );
};

// 필터 칩으로 노출되는 전체 상태 (배송완료 포함)
const FILTERABLE_STATUSES = [
  GiftDeliveryStatus.WaitingAddress,
  GiftDeliveryStatus.AddressSubmitted,
  GiftDeliveryStatus.Preparing,
  GiftDeliveryStatus.Shipping,
  GiftDeliveryStatus.Delivered,
  // 취소 건도 목록에 포함되어 감사카드 조회가 가능하도록 필터 노출
  GiftDeliveryStatus.Canceled,
];

// 기본 선택: 진행중 상태만. 주소입력대기는 배송지 미정이라 기본 제외(칩으로는 선택 가능)
const DEFAULT_STATUSES: GiftDeliveryStatus[] = [
  GiftDeliveryStatus.AddressSubmitted,
  GiftDeliveryStatus.Preparing,
  GiftDeliveryStatus.Shipping,
];

// 칩에 건수를 노출할 "처리 대기/진행중" 상태 (완료·취소는 누적이라 제외)
const COUNTABLE_STATUSES: GiftDeliveryStatus[] = [
  GiftDeliveryStatus.WaitingAddress,
  GiftDeliveryStatus.AddressSubmitted,
  GiftDeliveryStatus.Preparing,
  GiftDeliveryStatus.Shipping,
];

// 발주를 기다리는 "신규 주문" 상태 (배송지 입력이 끝나 바로 발주 가능한 건)
const NEW_ORDER_STATUS = GiftDeliveryStatus.AddressSubmitted;

export const GiftBrandDeliveryManagePage: FC = function GiftBrandDeliveryManagePage() {
  // 여러 상태를 동시에 선택 가능 (다중 선택)
  const [statusFilter, setStatusFilter] =
    useState<GiftDeliveryStatus[]>(DEFAULT_STATUSES);
  const [searchText, setSearchText] = useState('');
  // admin 전용: 브랜드(업체)별 필터 ('all' = 전체, NO_BRAND = 빈업체)
  const NO_BRAND = '__none__';
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [revertingId, setRevertingId] = useState<string | null>(null);
  // 배송완료 건 다중 선택 → 완료시각 일괄 변경
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [shippingModal, setShippingModal] = useState<ShippingModalState | null>(null);
  const [deliverModal, setDeliverModal] = useState<DeliverModalState | null>(null);
  const [cardDelivery, setCardDelivery] = useState<GiftDeliveryForAdmin | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Admin/GiftAgent는 전 브랜드(admin), 브랜드셀러는 본인 브랜드(gift-brand-seller)로 조회
  const { giftBrandRole } = useAuth();
  const role = giftBrandRole() as LowerRole.Admin | LowerRole.GiftBrandSeller;
  const isAdminView = role === LowerRole.Admin;

  // 감사카드 템플릿 (templateId 매칭용). Admin/GiftAgent는 전 브랜드, 셀러는 본인 브랜드.
  useGiftItemBrandListHook(role);
  const brands = useRecoilValue(giftItemBrandListDataAtom);
  const thanksTemplates = useMemo(
    () => brands?.[0]?.thanksPrint?.templates ?? [],
    [brands],
  );

  const [{ loading, value: deliveries }, fetchDeliveries] = useAsyncFn(
    async (statuses: GiftDeliveryStatus[]) => {
      // 선택된 상태가 없으면 빈 목록 (전체 이력 로드 방지)
      if (!statuses.length) return [];
      return listGiftBrandSellerDeliveries(
        { deliveryStatus: { $in: statuses } },
        { createdAt: -1 },
        role,
      );
    },
    [role],
  );

  // 칩 뱃지용 상태별 건수. 선택 필터와 무관하게 항상 전체 대기 물량을 보여준다.
  const [{ value: statusCounts }, fetchCounts] = useAsyncFn(async () => {
    const entries = await Promise.all(
      COUNTABLE_STATUSES.map(
        async (s) =>
          [s, await countGiftBrandSellerDeliveries({ deliveryStatus: s }, role)] as const,
      ),
    );
    return Object.fromEntries(entries) as Partial<Record<GiftDeliveryStatus, number>>;
  }, [role]);

  // 목록 + 건수를 함께 갱신 (상태 변경 후/새로고침에서 사용)
  const refreshAll = () => Promise.all([fetchDeliveries(statusFilter), fetchCounts()]);

  useEffect(() => {
    fetchDeliveries(statusFilter);
    fetchCounts();
  }, [fetchDeliveries, fetchCounts, statusFilter]);

  // 발주 대기 중인 신규 주문 건수 (배너/강조 트리거)
  const newOrderCount = statusCounts?.[NEW_ORDER_STATUS] ?? 0;

  const toggleStatus = (s: GiftDeliveryStatus) =>
    setStatusFilter((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );

  const copyAddress = async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      alert('복사에 실패했습니다.');
    }
  };

  // admin 전용: 현재 목록에 존재하는 브랜드 목록 (필터 드롭다운용, 중복 제거)
  const brandOptions = useMemo(() => {
    if (!isAdminView) return [];
    const map = new Map<string, string>();
    let hasNoBrand = false;
    for (const d of deliveries ?? []) {
      if (typeof d.giftItemBrand === 'object' && d.giftItemBrand?._id) {
        map.set(d.giftItemBrand._id, d.giftItemBrand.name);
      } else {
        hasNoBrand = true;
      }
    }
    const options = Array.from(map, ([id, name]) => ({ id, name }));
    // 업체가 없는 건도 '빈업체'로 필터 가능하게 (항상 마지막)
    if (hasNoBrand) options.push({ id: NO_BRAND, name: '빈업체' });
    return options;
  }, [deliveries, isAdminView, NO_BRAND]);

  const rows = useMemo(() => {
    // 상태 필터는 서버 조회(deliveryStatus $in)로 처리되므로 여기서는 검색/브랜드만 적용
    let result = [...(deliveries ?? [])];
    if (isAdminView && brandFilter !== 'all') {
      result = result.filter((d) => {
        const hasBrand = typeof d.giftItemBrand === 'object' && !!d.giftItemBrand?._id;
        return brandFilter === NO_BRAND
          ? !hasBrand
          : hasBrand &&
              typeof d.giftItemBrand === 'object' &&
              d.giftItemBrand?._id === brandFilter;
      });
    }
    if (searchText) {
      const search = searchText.toLowerCase().replace(/-/g, '');
      result = result.filter((d) => {
        const phone = d.recipient?.phoneNumber?.replace(/-/g, '') || '';
        const name = d.recipient?.name?.toLowerCase() || '';
        const item = getItemName(d).toLowerCase();
        return phone.includes(search) || name.includes(search) || item.includes(search);
      });
    }
    return result;
  }, [deliveries, searchText, isAdminView, brandFilter, NO_BRAND]);

  // 현재 목록 중 배송완료 건 (일괄 완료시각 변경 대상)
  const deliveredRows = useMemo(
    () => rows.filter((d) => d.deliveryStatus === GiftDeliveryStatus.Delivered),
    [rows],
  );
  const allDeliveredSelected =
    deliveredRows.length > 0 && deliveredRows.every((d) => selectedIds.has(d._id));

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSelectAllDelivered = () =>
    setSelectedIds(
      allDeliveredSelected ? new Set() : new Set(deliveredRows.map((d) => d._id)),
    );

  // 선택한 배송완료 건들의 완료시각을 한 번에 변경하는 모달 열기
  const openBulkDeliveredEdit = () => {
    const ids = Array.from(selectedIds);
    const first = ids[0];
    if (!first) return;
    setDeliverModal({
      deliveryId: first,
      bulkIds: ids,
      deliveredAt: nowForInput(),
      isEdit: true,
    });
  };

  // 다음 단계로 상태 변경
  const advanceStatus = async (delivery: GiftDeliveryForAdmin) => {
    const next = nextStatusMap[delivery.deliveryStatus];
    if (!next) return;

    // 준비중 -> 배송중 은 송장 정보를 받는다
    if (next === GiftDeliveryStatus.Shipping) {
      setShippingModal({
        deliveryId: delivery._id,
        carrier: delivery.shipping?.carrier || '',
        trackingNumber: delivery.shipping?.trackingNumber || '',
        mode: 'advance',
      });
      return;
    }

    // 배송중 -> 배송완료 는 배송완료 일시를 입력받는다 (기본값: 현재 시각)
    if (next === GiftDeliveryStatus.Delivered) {
      setDeliverModal({
        deliveryId: delivery._id,
        deliveredAt: nowForInput(),
      });
      return;
    }

    setProcessingId(delivery._id);
    try {
      await updateGiftDeliveryStatus(delivery._id, next, undefined, role);
      await refreshAll();
    } catch (err) {
      console.error(err);
      alert('상태 변경에 실패했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  // 이미 배송완료된 건의 완료시각만 수정 (송장번호와 무관, 상태 유지)
  const openDeliveredEdit = (delivery: GiftDeliveryForAdmin) => {
    setDeliverModal({
      deliveryId: delivery._id,
      deliveredAt: delivery.shipping?.deliveredAt
        ? isoToKstInput(delivery.shipping.deliveredAt)
        : nowForInput(),
      isEdit: true,
    });
  };

  // 이미 배송중/배송완료인 건의 송장만 추가/수정 (상태 전환 없음)
  const openShippingEdit = (delivery: GiftDeliveryForAdmin) => {
    setShippingModal({
      deliveryId: delivery._id,
      carrier: delivery.shipping?.carrier || '',
      trackingNumber: delivery.shipping?.trackingNumber || '',
      mode: 'edit',
      status: delivery.deliveryStatus,
      // 기존 발송/완료 시각을 보존 (shipping 서브도큐먼트 전체가 교체되므로)
      shippedAt: delivery.shipping?.shippedAt
        ? new Date(delivery.shipping.shippedAt).toISOString()
        : undefined,
      deliveredAt: delivery.shipping?.deliveredAt
        ? new Date(delivery.shipping.deliveredAt).toISOString()
        : undefined,
    });
  };

  const submitShipping = async () => {
    if (!shippingModal) return;
    const isEdit = shippingModal.mode === 'edit';
    // 수정 모드는 상태를 그대로 유지, 전환 모드는 배송중으로
    const targetStatus =
      isEdit && shippingModal.status ? shippingModal.status : GiftDeliveryStatus.Shipping;
    setProcessingId(shippingModal.deliveryId);
    try {
      await updateGiftDeliveryStatus(
        shippingModal.deliveryId,
        targetStatus,
        {
          carrier: shippingModal.carrier.trim() || undefined,
          trackingNumber: shippingModal.trackingNumber.trim() || undefined,
          // 전환 모드는 발송시각=현재, 수정 모드는 기존 시각 보존
          shippedAt: isEdit ? shippingModal.shippedAt : new Date().toISOString(),
          deliveredAt: isEdit ? shippingModal.deliveredAt : undefined,
        },
        role,
      );
      setShippingModal(null);
      await refreshAll();
    } catch (err) {
      console.error(err);
      alert(isEdit ? '송장 수정에 실패했습니다.' : '송장 등록에 실패했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  const submitDeliver = async () => {
    if (!deliverModal) return;
    if (!deliverModal.deliveredAt) {
      alert('배송완료 일시를 입력해주세요.');
      return;
    }
    const ids = deliverModal.bulkIds ?? [deliverModal.deliveryId];
    // 부분 입력(예: 연도 미완성) 등 잘못된 일시 방어
    const parsed = new Date(`${deliverModal.deliveredAt}:00+09:00`);
    if (!DT_RE.test(deliverModal.deliveredAt) || Number.isNaN(parsed.getTime())) {
      alert('배송완료 일시를 올바르게 입력해주세요.');
      return;
    }
    const iso = parsed.toISOString();
    const isBulk = !!deliverModal.bulkIds;
    if (isBulk) setBulkProcessing(true);
    else setProcessingId(deliverModal.deliveryId);
    try {
      let fail = 0;
      for (const id of ids) {
        try {
          await updateGiftDeliveryStatus(
            id,
            GiftDeliveryStatus.Delivered,
            { deliveredAt: iso },
            role,
          );
        } catch (err) {
          console.error(err);
          fail++;
        }
      }
      setDeliverModal(null);
      if (isBulk) setSelectedIds(new Set());
      await refreshAll();
      if (fail) {
        alert(`완료시각 변경 실패 ${fail}건 (성공 ${ids.length - fail}건)`);
      }
    } finally {
      setBulkProcessing(false);
      setProcessingId(null);
    }
  };

  // 실수로 진행한 상태 변경을 이전 단계로 되돌린다 (admin / gift-brand-seller 공통)
  const revertStatus = async (delivery: GiftDeliveryForAdmin) => {
    const prev = prevStatusMap[delivery.deliveryStatus];
    if (!prev) return;
    const label = prevStatusLabel[delivery.deliveryStatus] || '이전 상태로 되돌리기';
    if (!confirm(`${label} 하시겠습니까?`)) return;
    setRevertingId(delivery._id);
    try {
      await updateGiftDeliveryStatus(delivery._id, prev, undefined, role);
      await refreshAll();
    } catch (err) {
      console.error(err);
      alert('상태 되돌리기에 실패했습니다.');
    } finally {
      setRevertingId(null);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-bold">주문 배송관리</h2>
        <p className="mt-1 text-sm text-gray-500">
          신규 주문을 발주 → 배송 → 완료 단계로 처리하세요.
        </p>
      </div>

      {/* 필터 */}
      <div className="mb-4 space-y-2">
        {/* 상태 다중 선택 칩 (대기/진행중 상태는 현재 건수를 함께 노출) */}
        <div className="flex flex-wrap items-center gap-1.5">
          {FILTERABLE_STATUSES.map((s) => {
            const active = statusFilter.includes(s);
            const count = COUNTABLE_STATUSES.includes(s) ? statusCounts?.[s] : undefined;
            const isNewChip = s === NEW_ORDER_STATUS;
            // 신규 주문은 건수가 있으면 눈에 띄게(로즈), 그 외엔 상태색에 맞춰 표시
            const countClass =
              isNewChip && count
                ? 'bg-rose-500 text-white'
                : active
                ? 'bg-white/25 text-white'
                : 'bg-gray-100 text-gray-500';
            return (
              <button
                key={s}
                onClick={() => toggleStatus(s)}
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition ${
                  active
                    ? 'border-indigo-600 bg-indigo-600 text-white'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {statusLabel[s]?.text ?? s}
                {count !== undefined && (
                  <span
                    className={`inline-flex min-w-[1.125rem] justify-center rounded-full px-1 text-[10px] font-bold leading-4 ${countClass}`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdminView && (
            <select
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700"
            >
              <option value="all">전체 브랜드</option>
              {brandOptions.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          )}
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="수령인 · 연락처 · 상품명"
            className="flex-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm"
          />
          <button
            onClick={() => refreshAll()}
            className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
          >
            새로고침
          </button>
        </div>
      </div>

      {/* 신규 주문(발주 대기) 알림 배너 — 빠른 처리 유도 */}
      {newOrderCount > 0 && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="animate-pulse rounded bg-rose-500 px-2 py-0.5 text-xs font-bold text-white">
              NEW
            </span>
            <p className="text-sm font-semibold text-emerald-900">
              신규 주문 <b className="text-rose-600">{newOrderCount}건</b>이 접수를
              기다리고 있어요. 빠르게 처리해 주세요!
            </p>
          </div>
          {!(statusFilter.length === 1 && statusFilter[0] === NEW_ORDER_STATUS) && (
            <button
              onClick={() => setStatusFilter([NEW_ORDER_STATUS])}
              className="shrink-0 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
            >
              신규만 보기 →
            </button>
          )}
        </div>
      )}

      {/* 배송완료 다중 선택 시: 완료시각 일괄 변경 바 */}
      {selectedIds.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg bg-indigo-50 px-4 py-3">
          <span className="text-sm font-medium text-indigo-700">
            {selectedIds.size}건 선택됨
          </span>
          <button
            onClick={openBulkDeliveredEdit}
            disabled={bulkProcessing}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {bulkProcessing ? '처리 중...' : '완료시각 일괄 변경'}
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            선택 해제
          </button>
        </div>
      )}

      {loading ? (
        <div className="center-box py-16">
          <Loading />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
          {statusFilter.length === 0
            ? '조회할 상태를 선택해주세요.'
            : '처리할 배송 건이 없습니다.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-[11px] font-medium uppercase tracking-wide text-gray-400">
              <tr>
                <th className="px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={allDeliveredSelected}
                    onChange={toggleSelectAllDelivered}
                    disabled={deliveredRows.length === 0}
                    title="배송완료 건 전체 선택"
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-40"
                  />
                </th>
                <th className="px-3 py-2 text-left">순번</th>
                <th className="px-3 py-2 text-left">배송요청</th>
                <th className="px-3 py-2 text-left">상품</th>
                <th className="px-3 py-2 text-left">상품옵션</th>
                <th className="px-3 py-2 text-left">수량</th>
                <th className="px-3 py-2 text-left">수령인</th>
                <th className="px-3 py-2 text-left">상태</th>
                <th className="px-3 py-2 text-left">송장 / 배송완료</th>
                <th className="px-3 py-2 text-left">처리 / 감사카드</th>
                <th className="px-3 py-2 text-left">코드</th>
                <th className="px-3 py-2 text-left">주문자</th>
                {isAdminView && <th className="px-3 py-2 text-left">브랜드</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((d, idx) => {
                // 먼저 들어온(오래된) 주문이 1번. 목록은 최신순이므로 역순으로 번호 부여
                const seq = rows.length - idx;
                const orderer = getOrdererInfo(d);
                const badge = statusLabel[d.deliveryStatus] ?? {
                  text: d.deliveryStatus,
                  className: 'bg-gray-100 text-gray-600',
                };
                const canAdvance = !!nextStatusMap[d.deliveryStatus];
                const canRevert = !!prevStatusMap[d.deliveryStatus];
                // 발주 대기 신규 주문: 행 강조 + NEW 뱃지 + 발주 버튼 강조
                const isNew = d.deliveryStatus === NEW_ORDER_STATUS;
                const recipient = d.recipient;
                const fullAddress = recipient
                  ? `(${recipient.postalCode}) ${recipient.address} ${
                      recipient.addressDetail || ''
                    }`.trim()
                  : '';
                const requestedAt = formatRelativeDateTime(recipient?.submittedAt);
                const completedAt =
                  d.deliveryStatus === GiftDeliveryStatus.Delivered
                    ? formatRelativeDateTime(d.shipping?.deliveredAt)
                    : null;
                return (
                  <Fragment key={d._id}>
                    <tr
                      className={`align-middle ${
                        isNew
                          ? 'bg-emerald-50/70 hover:bg-emerald-50'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {/* 0. 선택 체크박스 (배송완료 건만 선택 가능) */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        {completedAt ? (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(d._id)}
                            onChange={() => toggleSelect(d._id)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        ) : null}
                      </td>
                      {/* 1. 순번 (신규 주문은 좌측 강조선) */}
                      <td
                        className={`whitespace-nowrap px-3 py-2.5 text-sm font-bold text-black ${
                          isNew ? 'border-l-4 border-emerald-500' : ''
                        }`}
                      >
                        {seq}
                      </td>
                      {/* 2. 배송요청 */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        {requestedAt ? (
                          <div className="leading-tight">
                            <span
                              className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                                requestedAt.relative === '오늘'
                                  ? 'bg-rose-100 text-rose-700'
                                  : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              {requestedAt.relative}
                            </span>
                            <p className="mt-0.5 text-sm font-bold text-gray-900">
                              {requestedAt.date}{' '}
                              <span className="text-xs font-medium text-gray-400">
                                {requestedAt.time}
                              </span>
                            </p>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-300">-</span>
                        )}
                      </td>
                      {/* 3. 상품 */}
                      <td className="px-3 py-2.5">
                        <p className="max-w-[220px] whitespace-normal break-words font-medium text-gray-800">
                          {getItemName(d)}
                        </p>
                        <span className="text-[11px] text-gray-400">
                          {typeLabel[d.type] || d.type}
                        </span>
                      </td>
                      {/* 4. 상품옵션 (선택한 추가옵션 — 출고 시 어떤 옵션 상품을 보낼지 확인) */}
                      <td className="px-3 py-2.5">
                        {d.selectedOptions && d.selectedOptions.length > 0 ? (
                          <ul className="max-w-[240px] space-y-1">
                            {d.selectedOptions.map((opt, i) => (
                              <li
                                key={`${opt.optionName}-${opt.slotIndex ?? i}`}
                                className="flex flex-wrap items-baseline gap-1.5 text-sm leading-tight"
                              >
                                <span className="text-gray-500">
                                  {opt.optionName}
                                  {/* selectionCount>1: 슬롯 번호를 1부터 표시 (비누종류 1, 2 …) */}
                                  {opt.slotIndex !== undefined
                                    ? ` ${opt.slotIndex + 1}`
                                    : ''}
                                </span>
                                <span className="font-semibold text-indigo-600">
                                  {opt.choiceName}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-xs text-gray-300">-</span>
                        )}
                      </td>
                      {/* 4. 수량 */}
                      <td className="whitespace-nowrap px-3 py-2.5 text-gray-700">
                        {d.quantity}개
                      </td>
                      {/* 5. 수령인 */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <p className="font-medium text-gray-800">
                          {d.recipient?.name || '-'}
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatPhone(d.recipient?.phoneNumber)}
                        </p>
                      </td>
                      {/* 6. 상태 (신규 주문은 NEW 뱃지로 강조) */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <div className="flex items-center gap-1">
                          {isNew && (
                            <span className="animate-pulse rounded bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                              NEW
                            </span>
                          )}
                          <span
                            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge.className}`}
                          >
                            {badge.text}
                          </span>
                        </div>
                        {isNew && (
                          <p className="mt-1 text-[10px] font-semibold text-emerald-600">
                            접수 대기중 · 바로 처리
                          </p>
                        )}
                      </td>
                      {/* 7. 송장 / 배송완료 */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        {/* 배송완료 건: 완료일 강조 (날짜 + 시각). 클릭 시 완료시각 수정 */}
                        {completedAt && (
                          <button
                            onClick={() => openDeliveredEdit(d)}
                            title="배송완료 시각 수정"
                            className="mb-1 block text-left text-sm font-bold leading-tight text-gray-900 underline decoration-dotted underline-offset-2 hover:text-indigo-600"
                          >
                            {completedAt.date}{' '}
                            <span className="text-xs font-medium text-gray-400">
                              {completedAt.time}
                            </span>
                          </button>
                        )}
                        {/* 송장/발송 정보 (배송중·배송완료) */}
                        {(d.deliveryStatus === GiftDeliveryStatus.Shipping ||
                          d.deliveryStatus === GiftDeliveryStatus.Delivered) && (
                          <div className="space-y-0.5 text-[11px] leading-tight text-gray-400">
                            {d.shipping?.carrier && (
                              <p className="text-gray-500">{d.shipping.carrier}</p>
                            )}
                            {d.shipping?.trackingNumber ? (
                              <button
                                onClick={() => openShippingEdit(d)}
                                title="송장 정보 수정"
                                className="block font-mono text-gray-500 underline decoration-dotted underline-offset-2 hover:text-gray-700"
                              >
                                {d.shipping.trackingNumber}
                              </button>
                            ) : (
                              <button
                                onClick={() => openShippingEdit(d)}
                                className="font-semibold text-amber-600 underline decoration-dotted underline-offset-2 hover:text-amber-700"
                              >
                                송장 미입력 (입력)
                              </button>
                            )}
                            {formatShortDateTime(d.shipping?.shippedAt) && (
                              <p>발송 {formatShortDateTime(d.shipping?.shippedAt)}</p>
                            )}
                          </div>
                        )}
                        {/* 그 외 상태: 표시할 배송정보 없음 */}
                        {d.deliveryStatus !== GiftDeliveryStatus.Shipping &&
                          d.deliveryStatus !== GiftDeliveryStatus.Delivered && (
                            <span className="text-xs text-gray-300">-</span>
                          )}
                      </td>
                      {/* 7. 처리 / 감사카드 */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {canAdvance ? (
                            <button
                              onClick={() => advanceStatus(d)}
                              disabled={processingId === d._id}
                              className={`rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${
                                isNew
                                  ? 'bg-emerald-600 shadow-sm hover:bg-emerald-700'
                                  : 'bg-indigo-600 hover:bg-indigo-700'
                              }`}
                            >
                              {processingId === d._id
                                ? '처리중...'
                                : isNew
                                ? '주문접수 →'
                                : nextStatusLabel[d.deliveryStatus] || '상태 변경'}
                            </button>
                          ) : (
                            !canRevert && (
                              <span className="text-xs text-gray-300">대기</span>
                            )
                          )}
                          {canRevert && (
                            <button
                              onClick={() => revertStatus(d)}
                              disabled={revertingId === d._id}
                              title={prevStatusLabel[d.deliveryStatus]}
                              className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                            >
                              {revertingId === d._id ? '처리중...' : '되돌리기'}
                            </button>
                          )}
                          {d.thanksPrint && (
                            <button
                              onClick={() => setCardDelivery(d)}
                              className="rounded-md border border-rose-200 px-2.5 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50"
                            >
                              감사카드
                            </button>
                          )}
                        </div>
                      </td>
                      {/* 8. 코드 */}
                      <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs text-gray-400">
                        {d.fullCode || d.code || '-'}
                      </td>
                      {/* 9. 주문자 */}
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <p className="text-gray-800">{orderer.name}</p>
                        <p className="text-xs text-gray-400">
                          {formatPhone(orderer.phone)}
                        </p>
                      </td>
                      {/* 10. 브랜드 (admin 전용, 우측 참조열) */}
                      {isAdminView && (
                        <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-600">
                          {typeof d.giftItemBrand === 'object'
                            ? d.giftItemBrand?.name
                            : '-'}
                        </td>
                      )}
                    </tr>
                    {/* 배송지: 같은 주문의 둘째 행. 신규 주문은 윗행과 동일 색으로 한 카드처럼 묶는다 */}
                    <tr
                      className={
                        isNew
                          ? 'bg-emerald-50/70 hover:bg-emerald-50'
                          : 'hover:bg-gray-50'
                      }
                    >
                      <td
                        colSpan={isAdminView ? 13 : 12}
                        className={`px-3 py-2.5 align-middle ${
                          isNew
                            ? 'border-l-4 border-t border-dashed border-emerald-500 border-t-emerald-200'
                            : 'border-t border-dashed border-gray-100'
                        }`}
                      >
                        {recipient?.address ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">
                              배송지
                            </span>
                            <p className="min-w-0 text-xs leading-snug text-gray-600">
                              ({recipient.postalCode}) {recipient.address}
                              {recipient.addressDetail
                                ? ` ${recipient.addressDetail}`
                                : ''}
                            </p>
                            <button
                              onClick={() => copyAddress(d._id, fullAddress)}
                              title="주소 복사"
                              className={`shrink-0 rounded-md border px-2 py-1 text-[11px] font-medium transition ${
                                copiedId === d._id
                                  ? 'border-green-300 bg-green-50 text-green-600'
                                  : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {copiedId === d._id ? '✓ 복사됨' : '주소 복사'}
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">
                              배송지
                            </span>
                            <span className="text-xs text-gray-400">미입력</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 송장 입력 모달 */}
      {shippingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl">
            <h3 className="mb-4 text-base font-bold">
              {shippingModal.mode === 'edit' ? '송장 정보 수정' : '송장 정보 입력'}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-semibold">택배사</label>
                <input
                  value={shippingModal.carrier}
                  onChange={(e) =>
                    setShippingModal({
                      ...shippingModal,
                      carrier: e.target.value,
                    })
                  }
                  placeholder="예: CJ대한통운"
                  className="w-full rounded border px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold">송장번호</label>
                <input
                  value={shippingModal.trackingNumber}
                  onChange={(e) =>
                    setShippingModal({
                      ...shippingModal,
                      trackingNumber: e.target.value,
                    })
                  }
                  placeholder="송장번호"
                  className="w-full rounded border px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <button
                onClick={() => setShippingModal(null)}
                className="flex-1 rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={submitShipping}
                disabled={processingId === shippingModal.deliveryId}
                className="flex-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {shippingModal.mode === 'edit' ? '저장' : '배송중으로 변경'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 배송완료 일시 입력 모달 */}
      {deliverModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !bulkProcessing && setDeliverModal(null)}
        >
          <div
            className="relative w-full max-w-md rounded-lg bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setDeliverModal(null)}
              aria-label="닫기"
              className="absolute right-3 top-3 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h3 className="mb-4 pr-8 text-base font-bold">
              {deliverModal.bulkIds
                ? `배송완료 시각 일괄 변경 (${deliverModal.bulkIds.length}건)`
                : deliverModal.isEdit
                ? '배송완료 시각 수정'
                : '배송완료 처리'}
            </h3>
            <div>
              <label className="mb-1 block text-sm font-semibold">배송완료 일시</label>
              <KstDateTimeField
                value={deliverModal.deliveredAt}
                onChange={(v) => setDeliverModal({ ...deliverModal, deliveredAt: v })}
              />
              <p className="mt-1 text-xs text-gray-400">
                {deliverModal.bulkIds
                  ? `선택한 ${deliverModal.bulkIds.length}건의 완료시각을 동일하게 변경합니다. (송장번호 등 다른 정보는 유지)`
                  : deliverModal.isEdit
                  ? '송장번호 등 다른 정보는 그대로 두고 완료시각만 변경합니다.'
                  : '기본값은 현재 시각이며, 실제 배송완료 시각으로 수정할 수 있습니다.'}
              </p>
            </div>
            <div className="mt-5 flex gap-2">
              <button
                onClick={() => setDeliverModal(null)}
                className="flex-1 rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={submitDeliver}
                disabled={processingId === deliverModal.deliveryId || bulkProcessing}
                className="flex-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {deliverModal.bulkIds
                  ? `${deliverModal.bulkIds.length}건 적용`
                  : deliverModal.isEdit
                  ? '완료시각 저장'
                  : '배송완료로 변경'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 감사카드 미리보기 / 인쇄 */}
      <GiftBrandThanksCardModal
        delivery={cardDelivery}
        templates={thanksTemplates}
        onClose={() => setCardDelivery(null)}
      />
    </div>
  );
};
